// CockpitFrame.tsx — S1 (COCKPIT-ASSISTANT-SPEC.md). Wraps the personal
// pack's whole shell (App.tsx's wrapCockpit) exactly once, which is what
// lets atlas-cockpit's own state (draft text, conversation, scroll
// position) survive a tab switch: the column/bar/sheet rendered here are
// siblings of the part of the tree App.tsx replaces on every route change,
// never inside it.
//
// >=1280px: a fixed 360px right column. 900-1279px: the same column at
// 320px (the spec leaves this range to atlas-core's call; COCKPIT_WIDE_MIN/
// COCKPIT_BAR_MAX/COCKPIT_COLUMN_PX are the one place that call lives).
// <900px: no column — AtlasBar fixed at the bottom, opening AtlasSheet.
//
// ⚑ `{children}` RENDERS IN THE SAME WRAPPER (.cockpit-frame__app) IN EVERY
// MODE (atlas-core review, 2026-10-15). The first cut put `{children}`
// directly under `.cockpit-frame` in bar mode but inside an extra
// `.cockpit-frame__app` div in column mode — two different element shapes
// at the same child position, so Preact unmounted and remounted the WHOLE
// app subtree underneath every time the width crossed 900px (an in-progress
// fold, a tablet rotation), losing every screen's own state. Only the
// SECOND child (bar+sheet vs the column) is allowed to vary by mode now.
import { useEffect, useRef, useState } from 'preact/hooks';
import type { VNode } from 'preact';
import type { CockpitSurfaceKey } from '@vowric/shared';
import { useAtlasChannel } from '../hooks/useAtlasChannel';
import { useBuilderChannel } from '../hooks/useBuilderChannel';
import { useCockpitWidth, COCKPIT_COLUMN_PX } from '../hooks/useCockpitWidth';
import { useBreakpoint, isRail } from '../hooks/useBreakpoint';
import type { AtlasSendContext, CockpitOccupant } from '../lib/atlasChannel';
import { AtlasColumn } from './AtlasColumn';
import { AtlasBar } from './AtlasBar';
import { AtlasSheet } from './AtlasSheet';
import './CockpitFrame.css';

export type { CockpitOccupant };

export interface CockpitFrameProps {
  children: VNode;
  /** Who the column/bar/sheet talks to. Defaults to 'atlas' — the only
   * occupant actually wired (A1). A `builder:${string}` value renders the
   * SAME disconnected column, under that name; see AtlasColumn's header. */
  occupant?: CockpitOccupant;
  /** A1 — the tenant whose conversation the column holds. Absent: the
   * honest disconnected default (S1's behaviour). */
  tenantId?: string | null;
  /** A1 — where the person is: the screen, and the open record (the thread
   * on a conversation view; nothing elsewhere). Sent with each message. */
  context?: AtlasSendContext;
  /** eb659c8d — "Atlas can switch screens on request": called with a surface key when such a request
   * arrives live. The caller maps it through cockpitSurfaces (a key not there does nothing). */
  onNavigate?: (surface: CockpitSurfaceKey) => void;
}

export function CockpitFrame({ children, occupant = 'atlas', tenantId = null, context, onNavigate }: CockpitFrameProps) {
  // ONE instance for the lifetime of this component — see useAtlasChannel's
  // own header. CockpitFrame itself never unmounts across a route change
  // (App.tsx wraps the swapped content, not the other way around), so this
  // hook call site is what makes the state durable. Called UNCONDITIONALLY
  // with `tenantId` regardless of `occupant` — never gated on it — so a
  // trip through a builder occupant and back never disposes Atlas's own
  // channel, keeping its draft/conversation exactly as H2 already proved.
  const atlasChannel = useAtlasChannel(tenantId);
  // H4a — a SEPARATE channel, only for a builder occupant; `useBuilderChannel`
  // returns the honest disconnected default when `builderProject` is null
  // (every screen that is not a builder project one), so it costs nothing
  // on every other screen.
  const builderProject = occupant.startsWith('builder:') ? occupant.slice('builder:'.length) : null;
  const builderChannel = useBuilderChannel(tenantId, builderProject);
  const channel = builderProject ? builderChannel : atlasChannel;
  // The latest handler, without resubscribing on every render.
  const navigateRef = useRef(onNavigate);
  navigateRef.current = onNavigate;
  // eb659c8d is Atlas's own conversation requesting a navigate — ALWAYS
  // off `atlasChannel`, never off whichever channel the pane currently
  // shows: a navigate request Atlas's own background work raises while a
  // builder occupant is on screen must still be honoured.
  useEffect(() => atlasChannel.onNavigate?.((surface) => navigateRef.current?.(surface)), [atlasChannel]);
  const screen = context?.screen ?? null;
  const recordKey = (context?.recordIds ?? []).join(',');
  // Same reasoning as onNavigate just above: Atlas's own "where he is"
  // context is always `atlasChannel`'s, whichever occupant the pane shows.
  useEffect(() => {
    atlasChannel.setContext?.({ screen, recordIds: recordKey ? recordKey.split(',') : [] });
  }, [atlasChannel, screen, recordKey]);
  const width = useCockpitWidth();
  const [sheetOpen, setSheetOpen] = useState(false);

  // Below 900px the bar renders; whether it must ALSO clear a real phone
  // BottomNav depends on the UNRELATED nav breakpoint (useBreakpoint's own
  // 768/1120, not this file's 900/1280) — NavZone renders BottomNav
  // unconditionally on the phone/stack frame (App.tsx), never on the
  // rail/tablet frame. Between 768-899px the rail renders (SideRail, no
  // BottomNav) while the cockpit bar still shows, so the two breakpoints
  // genuinely disagree in that range and both must be read.
  const navBreakpoint = useBreakpoint();
  const hasBottomNav = width === 'bar' && !isRail(navBreakpoint);

  const frameClass = [
    'cockpit-frame',
    width === 'bar' ? 'cockpit-frame--bar' : 'cockpit-frame--column',
    hasBottomNav ? 'cockpit-frame--with-nav' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div class={frameClass} data-cockpit-width={width}>
      <div class="cockpit-frame__app">{children}</div>
      {width === 'bar' ? (
        <>
          <AtlasBar onOpen={() => setSheetOpen(true)} />
          {sheetOpen ? (
            <AtlasSheet
              channel={channel}
              occupant={occupant}
              onClose={() => setSheetOpen(false)}
            />
          ) : null}
        </>
      ) : (
        <div class="cockpit-frame__column" style={{ width: `${COCKPIT_COLUMN_PX[width]}px` }}>
          <AtlasColumn channel={channel} occupant={occupant} />
        </div>
      )}
    </div>
  );
}
